import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { downloadBase64File } from "@/inventory/lib/download"
import { ReportChart } from "@/supplies/components/report-chart"
import {
  ensureDefaultCategoriesFn,
  exportSupplyReportBreakdownCsvFn,
  exportSupplyReportSeriesCsvFn,
  getSupplyReportBreakdownsFn,
  getSupplyReportSeriesFn,
  getSupplyReportSummaryFn,
  listSupplyCategoriesFn,
  listSupplyItemsFn,
  listSupplyVendorsFn,
} from "@/supplies/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_supplies-layout/supplies/reports")({
  component: ReportsPage,
})

type Granularity = "day" | "week" | "month"

interface FilterForm {
  dateFrom: string
  dateTo: string
  categoryId: string
  vendorId: string
  itemId: string
  granularity: Granularity
}

function msToDateInput(ms: number): string {
  const d = new Date(ms)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, "0")
  const day = String(d.getUTCDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function defaultFilters(): FilterForm {
  const now = Date.now()
  const toDay = Date.UTC(
    new Date(now).getUTCFullYear(),
    new Date(now).getUTCMonth(),
    new Date(now).getUTCDate(),
  )
  const fromDay = toDay - 29 * 86_400_000
  return {
    dateFrom: msToDateInput(fromDay),
    dateTo: msToDateInput(toDay),
    categoryId: "",
    vendorId: "",
    itemId: "",
    granularity: "day",
  }
}

function dateInputToStartMs(value: string): number {
  return Date.parse(`${value}T00:00:00.000Z`)
}

function dateInputToEndMs(value: string): number {
  return Date.parse(`${value}T23:59:59.999Z`)
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function formatDollars(n: number): string {
  return `$${n.toFixed(2)}`
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function selectClassName() {
  return cn(
    "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] md:text-sm dark:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:cursor-not-allowed disabled:opacity-50",
  )
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== "") {
      next[key] = value
    }
  }
  return next as Partial<T>
}

function formToRpcFilters(form: FilterForm) {
  const categoryId = form.categoryId ? Number(form.categoryId) : undefined
  const vendorId = form.vendorId ? Number(form.vendorId) : undefined
  const itemId = form.itemId ? Number(form.itemId) : undefined
  return stripUndefined({
    dateFrom: dateInputToStartMs(form.dateFrom),
    dateTo: dateInputToEndMs(form.dateTo),
    categoryId:
      categoryId != null && Number.isFinite(categoryId)
        ? categoryId
        : undefined,
    vendorId:
      vendorId != null && Number.isFinite(vendorId) ? vendorId : undefined,
    itemId: itemId != null && Number.isFinite(itemId) ? itemId : undefined,
    granularity: form.granularity,
  }) as {
    dateFrom: number
    dateTo: number
    categoryId?: number
    vendorId?: number
    itemId?: number
    granularity: Granularity
  }
}

function ReportsPage() {
  const [form, setForm] = useState<FilterForm>(defaultFilters)
  const [applied, setApplied] = useState<FilterForm>(defaultFilters)
  const [exportError, setExportError] = useState<string | null>(null)

  const rpcFilters = useMemo(() => formToRpcFilters(applied), [applied])

  const seedQuery = useQuery({
    queryKey: ["supply-categories", "ensure-defaults"],
    queryFn: () => ensureDefaultCategoriesFn(),
  })

  const categoriesQuery = useQuery({
    queryKey: ["supply-categories", { includeInactive: true }],
    queryFn: () => listSupplyCategoriesFn({ data: { includeInactive: true } }),
    enabled: seedQuery.isSuccess,
  })

  const vendorsQuery = useQuery({
    queryKey: ["supply-vendors", { includeInactive: true }],
    queryFn: () => listSupplyVendorsFn({ data: { includeInactive: true } }),
  })

  const itemsQuery = useQuery({
    queryKey: ["supply-items", { pageSize: 200, status: "active-for-report" }],
    queryFn: () =>
      listSupplyItemsFn({ data: { isActive: true, pageSize: 200 } }),
  })

  const summaryQuery = useQuery({
    queryKey: ["supply-report-summary", rpcFilters],
    queryFn: () => getSupplyReportSummaryFn({ data: rpcFilters }),
  })

  const seriesQuery = useQuery({
    queryKey: ["supply-report-series", rpcFilters],
    queryFn: () => getSupplyReportSeriesFn({ data: rpcFilters }),
  })

  const breakdownsQuery = useQuery({
    queryKey: ["supply-report-breakdowns", rpcFilters],
    queryFn: () => getSupplyReportBreakdownsFn({ data: rpcFilters }),
  })

  const exportSeriesMutation = useMutation({
    mutationFn: () => exportSupplyReportSeriesCsvFn({ data: rpcFilters }),
    onMutate: () => setExportError(null),
    onSuccess: (payload) => downloadBase64File(payload),
    onError: (err) => setExportError(getErrorMessage(err)),
  })

  const exportBreakdownMutation = useMutation({
    mutationFn: () => exportSupplyReportBreakdownCsvFn({ data: rpcFilters }),
    onMutate: () => setExportError(null),
    onSuccess: (payload) => downloadBase64File(payload),
    onError: (err) => setExportError(getErrorMessage(err)),
  })

  const summary = summaryQuery.data
  const buckets = seriesQuery.data?.buckets ?? []
  const breakdowns = breakdownsQuery.data

  const hasActivity = buckets.some(
    (b) =>
      b.spendCents > 0 ||
      b.unpricedReceiveQty > 0 ||
      b.usageQty > 0 ||
      b.usageEventCount > 0,
  )

  const isLoading =
    summaryQuery.isLoading || seriesQuery.isLoading || breakdownsQuery.isLoading

  const queryError =
    summaryQuery.error || seriesQuery.error || breakdownsQuery.error

  const labels = buckets.map((b) => b.label)
  const spendValues = buckets.map((b) => b.spendCents / 100)
  const usageValues = buckets.map((b) => b.usageQty)
  const onHandValues = buckets.map((b) => b.onHandTotal)

  function applyFilters() {
    setApplied({ ...form })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Reports</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            Spend, usage, and on-hand trends from supply movements.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={
              exportSeriesMutation.isPending ||
              exportBreakdownMutation.isPending
            }
            onClick={() => exportSeriesMutation.mutate()}
          >
            {exportSeriesMutation.isPending
              ? "Downloading…"
              : "Export series CSV"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={
              exportSeriesMutation.isPending ||
              exportBreakdownMutation.isPending
            }
            onClick={() => exportBreakdownMutation.mutate()}
          >
            {exportBreakdownMutation.isPending
              ? "Downloading…"
              : "Export breakdown CSV"}
          </Button>
        </div>
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div className="grid gap-1.5">
            <Label htmlFor="report-date-from">From</Label>
            <Input
              id="report-date-from"
              type="date"
              value={form.dateFrom}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, dateFrom: e.target.value }))
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="report-date-to">To</Label>
            <Input
              id="report-date-to"
              type="date"
              value={form.dateTo}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, dateTo: e.target.value }))
              }
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="report-granularity">Granularity</Label>
            <select
              id="report-granularity"
              value={form.granularity}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  granularity: e.target.value as Granularity,
                }))
              }
              className={selectClassName()}
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="report-category">Category</Label>
            <select
              id="report-category"
              value={form.categoryId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, categoryId: e.target.value }))
              }
              className={selectClassName()}
            >
              <option value="">All categories</option>
              {(categoriesQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {!c.isActive ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="report-vendor">Vendor</Label>
            <select
              id="report-vendor"
              value={form.vendorId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, vendorId: e.target.value }))
              }
              className={selectClassName()}
            >
              <option value="">All vendors</option>
              {(vendorsQuery.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {!v.isActive ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="report-item">Item</Label>
            <select
              id="report-item"
              value={form.itemId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, itemId: e.target.value }))
              }
              className={selectClassName()}
            >
              <option value="">All items</option>
              {(itemsQuery.data?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" onClick={applyFilters}>
            Apply
          </Button>
          <p className="text-muted-foreground text-xs">Dates use UTC</p>
        </div>
      </div>

      {exportError ? (
        <p className="text-destructive text-sm" role="alert">
          {exportError}
        </p>
      ) : null}

      {queryError ? (
        <p className="text-destructive text-sm" role="alert">
          {getErrorMessage(queryError)}
        </p>
      ) : null}

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading report…</p>
      ) : null}

      {!isLoading && summary ? (
        <>
          {summary.unpricedReceiveQty > 0 ? (
            <output className="block rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
              {summary.unpricedReceiveQty} unpriced receive unit
              {summary.unpricedReceiveQty === 1 ? "" : "s"} in this range
              {summary.spendCents === 0
                ? " — enter unit cost on receives to track spend."
                : " (excluded from spend totals)."}
            </output>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total spend</CardDescription>
                <CardTitle className="text-2xl tabular-nums">
                  {formatMoney(summary.spendCents)}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Unpriced receive qty</CardDescription>
                <CardTitle className="text-2xl tabular-nums">
                  {summary.unpricedReceiveQty}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Usage qty</CardDescription>
                <CardTitle className="text-2xl tabular-nums">
                  {summary.usageQty}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Use events</CardDescription>
                <CardTitle className="text-2xl tabular-nums">
                  {summary.usageEventCount}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 text-muted-foreground text-xs">
                {summary.daysWithUse} of {summary.dayCount} days with use
              </CardContent>
            </Card>
          </div>

          {!hasActivity ? (
            <Card>
              <CardContent className="space-y-3 py-8 text-center">
                <p className="text-muted-foreground text-sm">
                  No receive or use activity in this range.
                </p>
                <Button asChild>
                  <Link to="/supplies/receive">Receive stock</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid gap-4 lg:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Spend over time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ReportChart
                      title="Spend"
                      labels={labels}
                      values={spendValues}
                      formatValue={formatDollars}
                      variant="bar"
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Usage over time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ReportChart
                      title="Usage qty"
                      labels={labels}
                      values={usageValues}
                      variant="bar"
                    />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      On-hand over time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ReportChart
                      title="On hand"
                      labels={labels}
                      values={onHandValues}
                      variant="line"
                    />
                  </CardContent>
                </Card>
              </div>

              {breakdowns ? (
                <div className="grid gap-4 lg:grid-cols-2">
                  <BreakdownTable
                    title="Top consumed"
                    headers={["Item", "Usage qty", "Events"]}
                    rows={breakdowns.topConsumed.map((r) => [
                      r.name,
                      String(r.usageQty),
                      String(r.usageEvents),
                    ])}
                  />
                  <BreakdownTable
                    title="Spend by vendor"
                    headers={["Vendor", "Spend", "Receive qty", "Unpriced"]}
                    rows={breakdowns.byVendor.map((r) => [
                      r.name,
                      formatMoney(r.spendCents),
                      String(r.receiveQty),
                      String(r.unpricedQty),
                    ])}
                  />
                  <BreakdownTable
                    title="Spend by category"
                    headers={["Category", "Spend", "Receive qty", "Usage"]}
                    rows={breakdowns.byCategory.map((r) => [
                      r.name,
                      formatMoney(r.spendCents),
                      String(r.receiveQty),
                      String(r.usageQty),
                    ])}
                  />
                  <BreakdownTable
                    title="Spend by item"
                    headers={["Item", "SKU", "Spend", "Usage", "Events"]}
                    rows={breakdowns.byItem.map((r) => [
                      r.name,
                      r.sku ?? "—",
                      formatMoney(r.spendCents),
                      String(r.usageQty),
                      String(r.usageEvents),
                    ])}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      ) : null}
    </div>
  )
}

function BreakdownTable({
  title,
  headers,
  rows,
}: {
  title: string
  headers: string[]
  rows: string[][]
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No rows</p>
        ) : (
          <table className="w-full min-w-[280px] text-left text-sm">
            <thead>
              <tr className="border-b text-muted-foreground">
                {headers.map((h) => (
                  <th key={h} className="px-2 py-1.5 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  // biome-ignore lint/suspicious/noArrayIndexKey: static table rows
                  key={i}
                  className="border-border/60 border-b last:border-0"
                >
                  {row.map((cell, j) => (
                    <td
                      // biome-ignore lint/suspicious/noArrayIndexKey: cell order fixed
                      key={j}
                      className="px-2 py-1.5 tabular-nums"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  )
}
